<script lang="ts">
	import { onDestroy, onMount } from 'svelte';
	import ClubDealSheets from '@app/components/projects/sheets/ClubDealSheets.svelte';
	import CooperativeSheets from '@app/components/projects/sheets/CooperativeSheets.svelte';
	import ForestSheets from '@app/components/projects/sheets/ForestSheets.svelte';
	import FundsSheets from '@app/components/projects/sheets/FundsSheets.svelte';
	import NRESheets from '@app/components/projects/sheets/NRESheets.svelte';
	import RealEstateSheets from '@app/components/projects/sheets/RealEstateSheets.svelte';
	import StartupSheets from '@app/components/projects/sheets/StartupSheets.svelte';
	import { isOperationUpdated, operationStore } from '@app/stores/operation';
	import { getOperationCategoryString } from '@app/models/api/enums/OperationCategoryEnum';
	import { t } from 'svelte-i18n';
	import OperationCategoryCircleIcon from '@app/components/OperationCategoryCircleIcon.svelte';
	import ProjectGenericHeader from '@app/components/projects/headers/ProjectGenericHeader.svelte';
	import { OperationStatusEnum } from '@app/models/api/enums/OperationStatusEnum';
	import { formatHTML } from '$lib/utils/strings';
	import type { OperationModel } from '@app/models/api/OperationModel';
	import type { InputRangeProps } from '$lib/types/props';
	import { TargetTypeEnum } from '@app/models/api/enums/TypeCibleEnum';
	import PageHead from '@app/components/PageHead.svelte';
	import AdaptiveImage from '$lib/components/AdaptiveImage.svelte';
	import ProjectShortDescription from '@app/components/projects/elements/ProjectShortDescription.svelte';
	import ProgressIndicator from '$lib/elements/ProgressIndicator.svelte';
	import { page } from '$app/stores';
	import { consentVendorsStore } from '@app/stores/consent';
	import { hasConsentMatomoVendor } from '@app/utils/didomi';

	export let data;

	let operationProps: OperationModel;
	$: operationProps = data.operation;
	let sliderIntentionProps: InputRangeProps;
	let projectComponent;
	let voteModal;
	let _paq;

	const getProjectComponent = async () => {
		switch (operationProps?.targetType) {
			case TargetTypeEnum.COMPANY:
				projectComponent = StartupSheets;
				break;
			case TargetTypeEnum.REAL_ESTATE:
				projectComponent = RealEstateSheets;
				break;
			case TargetTypeEnum.COOPERATIVE:
				projectComponent = CooperativeSheets;
				break;
			case TargetTypeEnum.NRE:
				projectComponent = NRESheets;
				break;
			case TargetTypeEnum.FOREST:
				projectComponent = ForestSheets;
				break;
			case TargetTypeEnum.FUNDS:
				projectComponent = FundsSheets;
				break;
			case TargetTypeEnum.CLUB_DEAL:
				projectComponent = ClubDealSheets;
				break;
		}
	};

	const loadVoteModal = async () => {
		voteModal = (await import('@app/components/projects/ProjectVoteModal.svelte')).default;
	};

	const closeVoteModal = () => {
		voteModal = null;
	};

	$: operationProps && getProjectComponent();
	$: operationProps &&
		!sliderIntentionProps &&
		(sliderIntentionProps = {
			value: operationProps.intention ?? operationProps.minCoupon,
			min: operationProps.minCoupon,
			max: operationProps.maxCoupon,
			step: operationProps.subscriptionLevel
		});

	$: if ($page.url.searchParams.get('note') && $page.data.user) {
		loadVoteModal();
	}

	$: if ($isOperationUpdated) {
		operationProps = $operationStore;
		$isOperationUpdated = false;
	}

	$: if (hasConsentMatomoVendor($consentVendorsStore) && _paq && operationProps) {
		// Push Product View Data to Matomo - Populate parameters dynamically
		_paq.push([
			'setEcommerceView',
			operationProps.id, // (Required) productSKU
			operationProps.name, // (Optional) productName
			getOperationCategoryString(operationProps.category) // (Optional) categoryName
		]);
	}

	onMount(async () => {
		// MATOMO
		_paq = window._paq = window._paq || [];
		if (_paq && operationProps) {
			// You must also call trackPageView when tracking a product view
			_paq.push(['trackPageView']);
		}
	});
	onDestroy(() => {
		$operationStore = null;
	});
</script>

<PageHead
	title={$t('titles.projet', { values: { operationName: operationProps?.name ?? '' } })}
	shareImage={operationProps?.cover?.url}
	desc={operationProps?.description}
/>

<section>
	{#if operationProps}
		<div class="container relative mt-5 lg:mb-4">
			<div class="absolute top-0 left-1/2 transform -translate-y-1/2 -translate-x-1/2 z-10">
				<OperationCategoryCircleIcon category={operationProps.category} />
			</div>
			<div class="lg:flex">
				{#if operationProps.cover}
					<div
						class="h-[250px] lg:w-1/2 lg:h-[auto] lg:order-2 relative rounded-t-lg overflow-hidden {operationProps.status?.includes(
							OperationStatusEnum.VOTE
						) || operationProps.isOPC
							? 'lg:rounded-r-lg'
							: 'lg:rounded-tr-lg'} "
					>
						<div class="absolute inset-0">
							<AdaptiveImage
								src={operationProps.cover.url}
								alt={operationProps.cover.title}
								classNames="w-full h-full object-cover"
								isLazyLoaded={false}
							/>
						</div>
					</div>
				{/if}
				<div
					class="lg:w-1/2 lg:order-1 bg-dashboardBg p-4 xl:p-5 rounded-b-lg {operationProps.status?.includes(
						OperationStatusEnum.VOTE
					) || operationProps.isOPC
						? 'lg:rounded-l-lg lg:rounded-br-none'
						: 'lg:rounded-b-none lg:rounded-tl-lg'} "
				>
					<h1 class="uppercase">{operationProps.name}</h1>
					<ProjectShortDescription operation={operationProps} onVote={() => loadVoteModal()} />
				</div>
			</div>
			{#if !operationProps.status?.includes(OperationStatusEnum.VOTE) && !operationProps.isOPC}
				<div
					class="bg-secondary text-white mt-3 rounded-lg lg:mt-0 lg:rounded-t-none lg:rounded-b-lg p-3 lg:p-4 lg:mb-4"
				>
					<ProjectGenericHeader operation={operationProps} />
				</div>
			{/if}
			{#if operationProps.descriptionWiseed || operationProps.marketData}
				<div class="mb-4 bg-dashboardBg p-4 rounded-lg space-y-4 lg:gap-7 lg:flex lg:space-y-0">
					{#if operationProps.descriptionWiseed && operationProps.marketData}
						<div class="lg:w-2/3">
							<p class="font-semibold">{$t('operation.operationDescriptionTitle')}</p>
							<p>{@html formatHTML(operationProps.descriptionWiseed)}</p>
						</div>
						<div class="lg:w-1/3">
							<p class="font-semibold">{$t('operation.operationMarketDataTitle')}</p>
							<p>{@html formatHTML(operationProps.marketData)}</p>
						</div>
					{:else if operationProps.descriptionWiseed && !operationProps.marketData}
						<div class="lg:full">
							<p class="font-semibold">{$t('operation.operationDescriptionTitle')}</p>
							<p>{@html formatHTML(operationProps.descriptionWiseed)}</p>
						</div>
					{:else if !operationProps.descriptionWiseed && operationProps.marketData}
						<div class="lg:w-full">
							<p class="font-semibold">{$t('operation.operationMarketDataTitle')}</p>
							<p>{@html formatHTML(operationProps.marketData)}</p>
						</div>
					{/if}
				</div>
			{/if}
		</div>
		<div class="container">
			{#if projectComponent}
				<svelte:component this={projectComponent} operation={operationProps} slug={data.tab} />
			{/if}
		</div>
	{:else}
		<div class="py-4 text-center">
			<ProgressIndicator appearance="circular" size="huge" />
		</div>
	{/if}
</section>

<svelte:component
	this={voteModal}
	note={parseFloat(
		($page.url.searchParams.get('note') ?? 0) > 0 ? $page.url.searchParams.get('note') : 1
	)}
	operation={operationProps}
	on:closeModal={closeVoteModal}
/>

<style lang="postcss">
	.container :global(.tab) {
		@apply overflow-x-hidden;
	}
</style>
